import type { ApiErrorBody, ErrorCode, FieldError } from '@portal/contracts';

/** Error thrown for every non-2xx API response, carrying the server's machine-readable code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly requestId?: string,
    readonly fieldErrors?: FieldError[],
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Map server field errors to `{ [path]: message }` for form libraries. */
  fieldMap(): Record<string, string> {
    return Object.fromEntries((this.fieldErrors ?? []).map((f) => [f.path, f.message]));
  }
}

// Session-bound values live in memory only — never localStorage (CSRF token) — and are
// re-fetched from /auth/session on every page load.
let csrfToken: string | null = null;
let activeBusinessId: string | null = null;
export const setCsrfToken = (t: string | null) => void (csrfToken = t);
export const setActiveBusinessId = (id: string | null) => void (activeBusinessId = id);
export const getActiveBusinessId = () => activeBusinessId;

type Listener = (e: { type: 'unauthenticated' | 'mfa_required' }) => void;
const listeners = new Set<Listener>();
export const onAuthEvent = (fn: Listener) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Send X-Business-Id (default true). Set false for account-level endpoints. */
  business?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Return the Response untouched (downloads). */
  raw?: boolean;
  idempotencyKey?: string;
}

const BASE = '/api/v1';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return `${BASE}${path}${s ? `?${s}` : ''}`;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error */
  }
  const e = body?.error;
  return new ApiError(
    res.status,
    e?.code ?? (res.status === 429 ? 'rate_limited' : 'internal_error'),
    e?.message ?? (res.status === 429 ? 'Too many attempts. Please wait a moment.' : 'Something went wrong. Please try again.'),
    e?.requestId ?? res.headers.get('x-request-id') ?? undefined,
    e?.fieldErrors,
    e?.details,
  );
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;
  if (opts.business !== false && activeBusinessId) headers['X-Business-Id'] = activeBusinessId;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'same-origin',
      signal: opts.signal,
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network_error', 'We could not reach the server. Check your connection and try again.');
  }

  if (!res.ok) {
    const err = await toError(res);
    if (err.status === 401) listeners.forEach((l) => l({ type: err.code === 'mfa_required' ? 'mfa_required' : 'unauthenticated' }));
    throw err;
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const get = <T>(path: string, query?: RequestOptions['query'], o: Omit<RequestOptions, 'query' | 'method'> = {}) =>
  request<T>(path, { ...o, query });
export const post = <T = void>(path: string, body?: unknown, o: Omit<RequestOptions, 'body' | 'method'> = {}) =>
  request<T>(path, { ...o, method: 'POST', body });
export const patch = <T = void>(path: string, body?: unknown, o: Omit<RequestOptions, 'body' | 'method'> = {}) =>
  request<T>(path, { ...o, method: 'PATCH', body });
export const put = <T = void>(path: string, body?: unknown, o: Omit<RequestOptions, 'body' | 'method'> = {}) =>
  request<T>(path, { ...o, method: 'PUT', body });
export const del = <T = void>(path: string, o: Omit<RequestOptions, 'body' | 'method'> = {}) => request<T>(path, { ...o, method: 'DELETE' });

/** Fetch a protected file with the session + tenant headers (plain links cannot send X-Business-Id). */
export async function fetchBlob(path: string, query?: RequestOptions['query']): Promise<{ blob: Blob; fileName: string | null }> {
  const res = await request<Response>(path, { raw: true, query, headers: { Accept: '*/*' } });
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) ?? /filename="([^"]+)"/i.exec(cd);
  return { blob: await res.blob(), fileName: m?.[1] ? decodeURIComponent(m[1]) : null };
}

export async function downloadFile(path: string, fallbackName: string, query?: RequestOptions['query']): Promise<void> {
  const { blob, fileName } = await fetchBlob(path, query);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const newIdempotencyKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
